import dayjs from 'dayjs';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { UserStore } from './userStore';
import { AssetService } from '/src/api';
import { Asset, TAssetId } from '/src/lib/models';
import {
  AssetStatus,
  CreateAssetForm,
  ISODateTimeString,
  MenuItemType,
  TCreateAssetRequest,
  TListAssetsRequest,
  UserRole,
} from '/src/lib/types';

export interface AssetStoreProps {
  /**
   * List of Assets
   */
  assets?: Asset[];
  /**
   * Currently selected Asset
   */
  currentAsset?: Asset;
}

export class AssetStore extends BaseStore implements AssetStoreProps {
  private didInitMenu = false;
  userStore: UserStore;

  assets?: Asset[];
  currentAsset?: Asset;

  constructor(rootStore: RootStore) {
    super(rootStore);

    this.userStore = rootStore.userStore;

    makeObservable(this, {
      assets: observable,
      currentAsset: observable,
      setCurrentAsset: action,
    });
  }

  /**
   * Adds Asset nav menu items to the app header.
   */
  initMenuItems = () => {
    if (this.didInitMenu || !this.rootStore.userStore.user) return;

    // Add menu items
    this.rootStore.menuStore.addMenuItem(
      'topbar',
      'Assets',
      'assets',
      this.rootStore.userStore.isLender ? MenuItemType.Item : MenuItemType.Dropdown,
      '/assets(/create)?',
      false,
      undefined,
      8
    );
    if (!this.rootStore.userStore.isLender) {
      this.rootStore.menuStore.addSubMenuItem('topbar', 'assets', 'List Assets', 'assets');
      this.rootStore.menuStore.addSubMenuItem(
        'topbar',
        'assets',
        'New Asset',
        'assets/create',
        'assets/create',
        false,
        [UserRole.Admin, UserRole.Company]
      );
    }

    runInAction(() => (this.didInitMenu = true));
  };

  /**** Getters/Setters ****/

  /**
   * Sets the current Asset.
   *
   * @param asset The new current Asset. May be undefined
   * @returns The Asset
   */
  setCurrentAsset = (asset?: Asset) => (this.currentAsset = asset);

  /**** Data Processing ****/

  /**
   * Formats List Assets responses.
   *
   * @param assets - An array of Assets to format
   * @param userIsLender - Whether the logged-in User is a Lender User
   * @returns The formatted Assets
   */
  formatAssetList = (assets: Asset[], userIsAdmin: boolean) => {
    return assets.map((asset) => {
      // Process
      if (!asset.notes) {
        const notes = [...(userIsAdmin ? asset.socketComments ?? [] : []), ...(asset.companyComments ?? [])];

        // Sort comments by created date
        notes.sort((a, b) =>
          a.created && b.created ? (a.created > b.created ? 1 : b.created > a.created ? -1 : 0) : 0
        );

        // Save updated Notes array
        asset.notes = notes;
      }

      // Calculate
      asset.originalValue ??= 0;
      asset.currentAdjustments ??= 0;
      asset.initialPaymentAmount ??= 0;
      asset.adminFee ??= 0;
      asset.discountFee ??= 0;
      asset.lateFee ??= 0;
      asset.feeBalance ??= 0;

      asset.expectedResidualPayment =
        asset.originalValue +
        (asset.currentAdjustments || 0) -
        asset.initialPaymentAmount -
        asset.adminFee -
        asset.discountFee -
        asset.lateFee;

      // Parse
      asset.startDate = new Date(asset.startDate as unknown as ISODateTimeString);
      asset.nextDate = new Date(asset.nextDate as unknown as ISODateTimeString);
      asset.endDate = new Date(asset.endDate as unknown as ISODateTimeString);
      asset.effectiveDate = new Date(asset.effectiveDate as unknown as ISODateTimeString);

      // Return
      return asset;
    });
  };

  /**
   * Format Get Asset responses.
   *
   * @param asset - The Asset to format
   * @param userIsLender - Whether the logged-in User is a Lender User
   * @returns The formatted Asset
   */
  formatAsset = (asset: Asset, userIsLender: boolean) => {
    // Clean
    if (userIsLender) asset.files = [];

    // Total Payments
    if (asset.payments) {
      const currentPayments = asset.payments.map((payment) => payment.amount);
      if (currentPayments.length) asset.currentPayments = currentPayments.reduce((total, amount) => (total += amount));
    }

    // Total Adjustments
    if (asset.adjustments) {
      const currentAdjustments = asset.adjustments.map((adjustment) => adjustment.amount);
      if (currentAdjustments.length) {
        asset.currentAdjustments = currentAdjustments.reduce((total, amount) => (total += amount));
      }
    }

    // Total Starting Asset Value
    if (asset.adjustments) {
      const totalStartingAssetValue = asset.adjustments
        .filter((adjustment) => adjustment.assetValue)
        .map((adjustment) => adjustment.assetValue as number);

      if (totalStartingAssetValue.length) {
        asset.totalStartingAssetValue = totalStartingAssetValue.reduce((total, amount) => (total += amount));
      }
    }

    // Total Resulting Asset Value
    if (asset.adjustments) {
      let totalResultingAssetValue = 0;

      asset.adjustments = asset.adjustments.map((adjustment) => {
        const resultingAssetValue = (adjustment.assetValue ?? 0) + adjustment.amount;
        totalResultingAssetValue += resultingAssetValue;
        return { ...adjustment, resultingAssetValue };
      });

      asset.totalResultingAssetValue = totalResultingAssetValue;
    }

    // Calculate

    if (asset.feeBalance === undefined || asset.feeBalance === null || isNaN(asset.feeBalance)) asset.feeBalance = 0;

    asset.expectedResidualPayment =
      asset.originalValue +
      (asset.currentAdjustments || 0) -
      asset.initialPaymentAmount -
      asset.adminFee -
      asset.discountFee -
      asset.lateFee;

    asset.totalDueSocket = asset.initialPaymentAmount + asset.adminFee + asset.discountFee + asset.lateFee;

    // Parse
    asset.startDate = new Date(asset.startDate as unknown as ISODateTimeString);
    asset.nextDate = new Date(asset.nextDate as unknown as ISODateTimeString);
    asset.endDate = new Date(asset.endDate as unknown as ISODateTimeString);
    asset.effectiveDate = new Date(asset.effectiveDate as unknown as ISODateTimeString);

    // Return
    return asset;
  };

  /**** Asset CRUD ****/

  /**
   * Fetches a list of Assets.
   *
   * @param query - Optional query to send with the API request
   * @returns An array of Assets
   */
  query = async (query?: TListAssetsRequest) => {
    const response = await AssetService.query(query);

    runInAction(() => {
      this.assets = this.formatAssetList(response, !!this.userStore.isAdmin);
    });

    return this.assets;
  };

  /**
   * Fetches a single Asset and sets it as the current Asset.
   *
   * @param assetId - ID of the Asset to fetch
   * @returns An Asset object
   */
  get = async (assetId: TAssetId) => {
    const response = await AssetService.get(assetId);

    runInAction(() => {
      this.currentAsset = response ? this.formatAsset(response, !!this.userStore.isLender) : undefined;
    });

    return this.currentAsset;
  };

  /**
   * Fetches an Asset's name.
   *
   * @param assetId - ID of the Asset
   * @returns The Asset's name
   */
  getName = async (assetId: TAssetId) => {
    const response = await AssetService.get(assetId);

    return response;
  };

  /**
   * Creates an Asset.
   *
   * @param formValues - A populated CreateAssetForm object
   * @returns The new Asset
   */
  create = async (formValues: CreateAssetForm) => {
    if (!this.userStore.user) throw new Error('Not authorized');
    if (!formValues.loan || !formValues.effectiveDate || !formValues.nextDate)
      throw new Error('Please fix the errors first.');

    const loan = formValues.loan;
    const value = parseFloat(formValues.value);

    // Create new Asset object
    const asset: TCreateAssetRequest = {
      loan: loan._id,
      company: loan.borrower._id,
      name: formValues.name,
      assetType: formValues.assetType,
      value,
      originalValue: value,
      program: formValues.program,
      paymentSchedule: formValues.paymentSchedule,
      nextDate: dayjs(formValues.nextDate).toDate(),
      companyComments: formValues.companyComments,
      socketComments: formValues.socketComments,
      effectiveDate: dayjs(formValues.effectiveDate).toDate(),
      files: formValues.files,
      status: AssetStatus.Open,
    };

    if (loan.isOwned) {
      loan.adminFee ??= 0;
      loan.discountFee ??= 0;

      // Admin Fee
      asset.adminFee = value * loan.adminFee;

      // Discount Fee
      asset.discountFee =
        value * loan.discountFee * (dayjs(formValues.nextDate).diff(dayjs(formValues.effectiveDate), 'days') / 30);

      // Late Fee
      asset.lateFee = 0;

      // Initial Payment Amount
      asset.initialPaymentAmount = value * loan.advanceRate;

      // Late After Date
      asset.startDate = dayjs(formValues.nextDate).add(15, 'days').toDate();

      // Buyback Date
      asset.endDate = dayjs(formValues.nextDate).add(30, 'days').toDate();
    }

    const response = await AssetService.create(asset);

    return response;
  };

  /**
   * Updates an Asset.
   *
   * @param asset - The Asset to update
   * @returns The updated Asset
   */
  update = async (asset: Asset) => {
    if (!this.userStore.user) throw new Error('Not authorized');

    if (!asset.loan || !asset.assetType || !asset.effectiveDate || !asset.nextDate)
      throw new Error('Please fix the errors first.');

    const loan = asset.loan;

    // Re-calculate any fields depending on nextDate or effectiveDate
    if (loan.isOwned) {
      loan.adminFee ??= 0;
      loan.discountFee ??= 0;

      // Discount Fee
      asset.discountFee =
        asset.originalValue * loan.discountFee * (dayjs(asset.nextDate).diff(dayjs(asset.effectiveDate), 'days') / 30);

      // Late After Date
      asset.startDate = dayjs(asset.nextDate).add(15, 'days').toDate();

      // Buyback Date
      asset.endDate = dayjs(asset.nextDate).add(30, 'days').toDate();
    }

    asset.loan = asset.loan._id;

    const response = await AssetService.update(asset._id, asset);

    runInAction(() => (this.currentAsset = Object.assign(this.currentAsset ?? {}, response)));

    return this.currentAsset;
  };

  /**
   * Closes an Asset. This is equivalent to a soft delete.
   *
   * @param assetId - ID of the Asset to close
   * @returns The closed Asset
   */
  close = async (assetId: TAssetId) => {
    return await AssetService.remove(assetId);
  };
}
